import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { TProductInstance } from '@lib/core/products/types';
import { getProductsListData } from '@lib/core/products/utils';
import { ApiQueries, REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { selectUserCharactersForServiceProductCategory } from '@lib/core/users/selectors/characters';
import { selectCurrentGameProgress, selectGameDetails, selectGamesProgress } from '@lib/tools/gamification/selectors';
import {
  apiUrlCreateGameEnrollment,
  apiUrlGameEnrollments,
  apiUrlListAllRetailerGames,
  apiUrlListUserGameEnrollments,
  apiUrlUpdateGameProgress,
} from '@lib/tools/gamification/slices/urls';
import { TGame, TGameEnrollment, TGamesProgress, TReward } from '@lib/tools/gamification/types';

interface State {
  gameDetails: TGame | null;
  gameEnrollments: { rewards: TReward[]; totalScore: number } | null;
  gamesProgress: TGamesProgress;
  isLoading: boolean;
  products: TProductInstance[];
}

const initialState: State = {
  gameDetails: null,
  gameEnrollments: null,
  gamesProgress: {},
  isLoading: false,
  products: [],
};

export const actionInitGamification = createTypedAsyncThunk('actionInitGamification', async (_, { getState }) => {
  const { results: retailerGames = [] as TGame[] } = await request({
    url: apiUrlListAllRetailerGames(),
  });

  const currentGameDetails: TGame = retailerGames[0] ?? null;

  if (!currentGameDetails) return null;

  const { results: userGameEnrollments = [] as TGameEnrollment[] } = await request({
    url: apiUrlListUserGameEnrollments(),
  });

  const isCurrentGameRegistered = !!userGameEnrollments.find(game => game.game.slug === currentGameDetails.slug);

  if (!isCurrentGameRegistered) {
    await request({
      body: { game: currentGameDetails.slug },
      method: REQUEST_METHODS.POST,
      url: apiUrlCreateGameEnrollment(),
    });
  }

  const currentGameEnrollment: TGameEnrollment = await request({
    url: apiUrlGameEnrollments(currentGameDetails.slug),
  });

  const state = getState();
  const category = selectServiceProductCategory(state);
  const characterIds = (selectUserCharactersForServiceProductCategory(state) ?? [])
    .map(character => character?.characterId)
    .reverse();
  const usedProductIds = selectGamesProgress(state)?.[currentGameDetails.slug]?.products ?? [];

  const requiredCount =
    (currentGameDetails?.levels?.filter(l => !l.isOnboarding).reduce((sum, l) => sum + (l.steps?.length ?? 0), 0) ??
      0) + 1;
  const collected: TProductInstance[] = [];

  const loadProductsByCharacterIndex = async (index = 0): Promise<TProductInstance[]> => {
    if (collected.length >= requiredCount || index >= characterIds.length) {
      return collected.slice(0, requiredCount);
    }

    const params = {
      [ApiQueries.productCategory]: category,
      ...(characterIds[index] ? { [ApiQueries.character]: characterIds[index] } : {}),
    };

    const { results = [] } = await getProductsListData(params);
    const fresh = results.filter(p => !usedProductIds.includes(p.identifier));
    collected.push(...fresh);

    return loadProductsByCharacterIndex(index + 1);
  };

  const products = await loadProductsByCharacterIndex();

  return {
    currentGameDetails,
    currentGameEnrollment: {
      rewards: (currentGameEnrollment.rewards as TReward[]) ?? [],
      totalScore: currentGameEnrollment.totalScore ?? 0,
    },
    products,
  };
});

export const actionUpdateGamificationProgress = createTypedAsyncThunk(
  'actionUpdateGamificationProgress',
  async (payload: { levelSlug: string; stepIndex: number; productId: string }, { getState }) => {
    const state = getState();
    const currentGameDetails = selectGameDetails(state);
    if (!currentGameDetails) return null;

    const { levelSlug, stepIndex, productId } = payload;
    const currentLevel = currentGameDetails.levels.find(l => l.slug === levelSlug);
    if (!currentLevel) return null;

    const currentLevelStep = currentLevel.steps[stepIndex];
    if (!currentLevelStep) return null;

    const currentGameSlug = currentGameDetails.slug;
    const currentStepScore = currentLevelStep.score;
    const levelProgress = selectCurrentGameProgress(state)?.levels?.[levelSlug];

    const updatedLevelTotal = (levelProgress?.levelTotalScore ?? 0) + currentStepScore;
    const updatedGameTotal = (state.gamification.gameEnrollments?.totalScore ?? 0) + currentStepScore;

    const levelTarget =
      typeof currentLevel.score === 'number' && currentLevel.score > 0
        ? currentLevel.score
        : currentLevel.steps.reduce((sum, s) => sum + s.score, 0);

    const isAlreadyClaimed = Boolean(levelProgress?.rewardClaimed);
    const isRewardExist = Boolean(currentLevel.reward?.slug);
    const isLevelCompletedAfterStep = !isAlreadyClaimed && updatedLevelTotal >= levelTarget && isRewardExist;

    const response = await request({
      body: {
        totalScore: updatedGameTotal,
        ...(isLevelCompletedAfterStep && currentLevel.reward?.slug ? { rewards: [currentLevel.reward.slug] } : {}),
      },
      method: REQUEST_METHODS.PATCH,
      url: apiUrlUpdateGameProgress(currentGameSlug),
    });

    return {
      gameSlug: currentGameSlug,
      isLevelCompletedAfterStep,
      levelSlug,
      levelTarget,
      productId,
      serverRewards: (response?.rewards as TReward[]) ?? undefined,
      serverTotalScore: typeof response?.totalScore === 'number' ? (response.totalScore as number) : undefined,
      stepIndex,
      updatedLevelTotal,
    };
  },
);

export const gamificationSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(actionInitGamification.pending, state => {
        state.isLoading = true;
      })
      .addCase(actionInitGamification.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        if (!payload) return;

        const { currentGameDetails, currentGameEnrollment, products } = payload;

        state.gameDetails = currentGameDetails;
        state.gameEnrollments = currentGameEnrollment;
        state.products = products;

        // List of reward slugs that the user has already earned
        const rewardSlugs = (currentGameEnrollment?.rewards ?? []).map(r => r.slug);

        // Initialize or access the progress state for this game
        if (!state.gamesProgress[currentGameDetails.slug]) {
          state.gamesProgress[currentGameDetails.slug] = { levels: {}, products: [] };
        }
        const levelsProgress = state.gamesProgress[currentGameDetails.slug].levels;

        currentGameDetails.levels.forEach(level => {
          const previousLevelProgress = levelsProgress[level.slug];
          const stepsLength = level.steps.length;

          // Case 1: User already earned this reward — mark the level as fully completed
          if (rewardSlugs.includes(level?.reward?.slug)) {
            levelsProgress[level.slug] = {
              doneSteps: Array(stepsLength).fill(true),
              isOpened: levelsProgress[level.slug]?.isOpened ?? false,
              isValidated: levelsProgress[level.slug]?.isValidated ?? false,
              levelTotalScore: level.score,
              rewardClaimed: true,
            };
            return;
          }

          // Case 2: Level progress exists — sync it with the updated steps structure
          if (previousLevelProgress) {
            levelsProgress[level.slug] = {
              doneSteps: previousLevelProgress.doneSteps,
              isOpened: previousLevelProgress.isOpened ?? false,
              isValidated: previousLevelProgress.isValidated ?? false,
              levelTotalScore: previousLevelProgress.levelTotalScore ?? 0,
              rewardClaimed: previousLevelProgress.rewardClaimed,
            };

            // Case 3: New level — initialize an empty progress entry
          } else {
            levelsProgress[level.slug] = {
              doneSteps: Array(stepsLength).fill(false),
              isOpened: false,
              isValidated: false,
              levelTotalScore: 0,
              rewardClaimed: false,
            };
          }
        });
      })
      .addCase(actionInitGamification.rejected, state => {
        state.isLoading = false;
      });

    builder.addCase(actionUpdateGamificationProgress.fulfilled, (state, { payload }) => {
      if (!payload) return;

      const {
        gameSlug,
        levelSlug,
        stepIndex,
        levelTarget,
        updatedLevelTotal,
        isLevelCompletedAfterStep,
        serverTotalScore,
        serverRewards,
        productId,
      } = payload;

      const currentGame = state.gameDetails;
      if (!currentGame || currentGame.slug !== gameSlug) return;

      if (!state.gamesProgress[gameSlug]) state.gamesProgress[gameSlug] = { levels: {}, products: [] };
      const gameProgress = state.gamesProgress[gameSlug];
      const levelsProgress = gameProgress.levels;

      const currentLevel = currentGame.levels.find(l => l.slug === levelSlug);
      if (!currentLevel) return;

      const stepCount = currentLevel.steps.length;

      if (!levelsProgress[levelSlug]) {
        levelsProgress[levelSlug] = {
          doneSteps: Array(stepCount).fill(false),
          levelTotalScore: 0,
          rewardClaimed: false,
        };
      }

      const levelProgress = levelsProgress[levelSlug];

      if (!levelProgress.rewardClaimed) {
        const safeStepIndex =
          typeof stepIndex === 'number' && stepIndex >= 0 && stepIndex < stepCount ? stepIndex : null;

        if (safeStepIndex != null && !levelProgress.doneSteps[safeStepIndex]) {
          levelProgress.doneSteps[safeStepIndex] = true;
          levelProgress.levelTotalScore = Math.min(updatedLevelTotal, levelTarget);
        }

        if (isLevelCompletedAfterStep) {
          levelProgress.rewardClaimed = true;
          levelProgress.levelTotalScore = levelTarget;
          levelProgress.doneSteps = Array(stepCount).fill(true);
        }
      }

      if (typeof serverTotalScore === 'number' || Array.isArray(serverRewards)) {
        state.gameEnrollments = {
          rewards: serverRewards ?? state.gameEnrollments?.rewards ?? [],
          totalScore:
            typeof serverTotalScore === 'number' ? serverTotalScore : (state.gameEnrollments?.totalScore ?? 0),
        };
      }

      if (productId) {
        if (!Array.isArray(gameProgress.products)) gameProgress.products = [];
        if (!gameProgress.products.includes(productId)) {
          gameProgress.products.push(productId);
        }
      }
    });
  },
  initialState,
  name: 'gamification',
  reducers: {
    actionAddSelectedProduct(state, action: PayloadAction<TProductInstance>) {
      state.products = [action.payload, ...state.products];
    },
    actionResetGame: () => initialState,
    actionSetRewardOpened(state, action: PayloadAction<{ gameSlug: string; levelSlug: string }>) {
      const { gameSlug, levelSlug } = action.payload;
      const level = state.gamesProgress[gameSlug]?.levels?.[levelSlug];
      if (level) level.isOpened = true;
    },
    actionSetRewardValidated(state, action: PayloadAction<{ gameSlug: string; levelSlug: string }>) {
      const { gameSlug, levelSlug } = action.payload;
      const level = state.gamesProgress[gameSlug]?.levels?.[levelSlug];
      if (level) level.isValidated = true;
    },
  },
});

export const { actionAddSelectedProduct, actionResetGame, actionSetRewardOpened, actionSetRewardValidated } =
  gamificationSlice.actions;
export default gamificationSlice.reducer;
